import { useState, useEffect, useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useDateLocale } from '@/hooks/useDateLocale';
import { Loader2, Calendar, Clock, MapPin, CircleStop } from 'lucide-react';
import { format, startOfDay, endOfDay, subDays, formatDuration, intervalToDuration } from 'date-fns';
import { DateRange } from 'react-day-picker';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  fetchDevices,
  fetchStops,
  type Device,
  type Stop,
} from '@/lib/traccar-api';

const Stops = () => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateLocale();
  const { isAuthenticated } = useTraccarSession();

  const [devices, setDevices] = useState<Device[]>([]);
  const [stops, setStops] = useState<Stop[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStops, setIsLoadingStops] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: startOfDay(subDays(new Date(), 7)),
    to: endOfDay(new Date()),
  });

  const loadInitialData = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      const devicesData = await fetchDevices();

      if (devicesData.length > 0) {
        setDevices(devicesData);
        if (!selectedDeviceId && devicesData.length > 0) {
          setSelectedDeviceId(devicesData[0].id);
        }
      }
      setHasLoaded(true);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, selectedDeviceId]);

  const loadStops = useCallback(async () => {
    if (!selectedDeviceId || !dateRange?.from || !dateRange?.to) return;

    try {
      setIsLoadingStops(true);
      const from = format(dateRange.from, "yyyy-MM-dd'T'HH:mm:ss'Z'");
      const to = format(dateRange.to, "yyyy-MM-dd'T'HH:mm:ss'Z'");

      const stopsData = await fetchStops(selectedDeviceId, from, to);
      setStops(stopsData);
    } catch (error) {
      console.error('Failed to load stops:', error);
      setStops([]);
    } finally {
      setIsLoadingStops(false);
    }
  }, [selectedDeviceId, dateRange]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (selectedDeviceId && dateRange?.from && dateRange?.to) {
      loadStops();
    }
  }, [selectedDeviceId, dateRange, loadStops]);

  const formatStopDuration = (ms: number) => {
    const duration = intervalToDuration({ start: 0, end: ms });
    return formatDuration(duration, { format: ['hours', 'minutes'], zero: false, locale: dateFnsLocale }) || '< 1 min';
  };

  const totals = useMemo(() => {
    return stops.reduce(
      (acc, stop) => ({
        duration: acc.duration + (stop.duration || 0),
        stopCount: acc.stopCount + 1,
      }),
      { duration: 0, stopCount: 0 }
    );
  }, [stops]);

  if (isLoading && !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const selectedDevice = devices.find(d => d.id === selectedDeviceId);

  const headerContent = (
    <div className="flex items-center gap-4">
      <Select
        value={selectedDeviceId?.toString() ?? ""}
        onValueChange={(value) => setSelectedDeviceId(parseInt(value, 10))}
      >
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder={t('history.selectDevice')} />
        </SelectTrigger>
        <SelectContent>
          {devices.map((device) => (
            <SelectItem key={device.id} value={device.id.toString()}>
              {device.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Calendar className="h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, "MMM d", { locale: dateFnsLocale })} - {format(dateRange.to, "MMM d, yyyy", { locale: dateFnsLocale })}
                </>
              ) : (
                format(dateRange.from, "MMM d, yyyy", { locale: dateFnsLocale })
              )
            ) : (
              t('history.selectDates')
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComponent
            mode="range"
            selected={dateRange}
            onSelect={setDateRange}
            numberOfMonths={2}
            disabled={(date) => date > new Date()}
          />
        </PopoverContent>
      </Popover>
    </div>
  );

  return (
    <DashboardLayout headerContent={headerContent}>
      <div className="p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('history.totalStops') || 'Total Stops'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <CircleStop className="h-5 w-5 text-primary" />
                <span className="text-2xl font-bold">{totals.stopCount}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{t('history.totalDuration')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                <span className="text-2xl font-bold">{formatStopDuration(totals.duration)}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CircleStop className="h-5 w-5" />
              {t('nav.stops')}
              {selectedDevice && (
                <Badge variant="secondary" className="ml-2">
                  {selectedDevice.name}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingStops ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : stops.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CircleStop className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t('history.noStops') || 'No stops found'}</p>
                <p className="text-sm mt-1">{t('history.noStopsHint') || 'Try adjusting your date range'}</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                {stops.map((stop, index) => (
                  <div
                    key={`${stop.deviceId}-${stop.startTime}-${index}`}
                    className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center gap-2 text-sm">
                        <CircleStop className="h-4 w-4 text-red-500" />
                        {format(new Date(stop.startTime), 'MMM d, HH:mm', { locale: dateFnsLocale })}
                      </div>
                      <span className="text-muted-foreground">-</span>
                      <div className="text-sm text-muted-foreground">
                        {format(new Date(stop.endTime), 'HH:mm')}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatStopDuration(stop.duration)}
                      </span>
                    </div>
                    {stop.address && (
                      <div className="mt-2 flex items-start gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <span className="text-muted-foreground">{stop.address}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Stops;
